// React
import { Fragment, useContext, useEffect, useState } from "react"

// Components
import { AccountModal, BaseTable, Card, DateInput, HeaderMenu, Input, Select, TableHeader, Textarea } from "components"

// Contexts
import { DisabledContext } from "pages/Purchase/Transaction/contexts"
import {  ProductUnitProvider } from "pages/Purchase/Transaction/PurchaseOrder/components/DetailBody/contexts"
import { DetailDataContext, ProductContext, ProductProvider } from "../contexts"

// Form
import { Controller, useFormContext, useWatch } from "react-hook-form"

// Third-Party Libraries
import moment from "moment"

// Types
import type { DataCountDetail } from "../../types"

// Utils
import { convertNumber, useEmployee, useToggle } from "utils"
import { type FormType, useDataCode } from "../utils"
import { PropsValue } from "react-select"

export function FormSection(params: { 
  footerElement?: JSX.Element
}): JSX.Element {
  // Form
  const disabled = useContext(DisabledContext)
  const { control } = useFormContext<FormType>()
  const { invcount, countdetail } = useContext(DetailDataContext)
  return (
    <section className="container my-5 flex flex-col gap-5">
      <HeaderMenu title="DATA ENTRY | ADJUSTMENT INVENTORY">
        <Code />
      </HeaderMenu>

      <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
        <section className="flex flex-col gap-3">
          <Input
            disabled
            label="LOCATION"
            value={invcount.location_name}
          />

          <Input
            disabled
            label="REQUEST BY"
            placeholder="Select Employee"
            value={invcount.employee_name}
          />

          <DateInput
            disabled
            label="COUNT DATE"
            selected={moment(invcount.transaction_date).toDate()}
            onChange={() => { }}
          />

          <Textarea
            disabled
            label="COUNT MEMO"
            value={invcount.memo}
          />

          <Input
            disabled
            label="LINK COUNT ID#"
            value={invcount.trx_code}
          />
        </section>

        <section className="flex flex-col gap-3">
          <Employee />

          <Controller
            name="transaction_date"
            control={control}
            render={({ field, fieldState }) => (
              <DateInput
                label="ADJUSTMENT DATE"
                disabled={disabled}
                error={fieldState.error?.message}
                ref={field.ref}
                selected={moment(field.value).toDate()}
                onChange={value => field.onChange(moment(value).format("YYYY-MM-DD"))}
              />
            )}
          />

          <Controller
            name="memo"
            control={control}
            render={({ field, fieldState }) => (
              <Textarea
                {...field}
                label="ADJUSTMENT MEMO"
                disabled={disabled}
                error={fieldState.error?.message}
              />
            )}
          />
        </section>
      </section>

      <Card>
        <Card.Body className="flex flex-col gap-3 overflow-x-auto">
          <div className="text-xl font-bold">LIST ITEM TRANSFERED</div>

          <BaseTable>
            <TableHeader>
              <tr>
                <th rowSpan={2}>#</th>
                <th rowSpan={2}>ITEM</th>
                <th rowSpan={2}>DESCRIPTION</th>
                <th rowSpan={2}>UNIT</th>
                <th rowSpan={2}>SOH</th>
                <th rowSpan={2}>STOCK IN</th>
                <th rowSpan={2}>STOCK OUT</th>
                <th colSpan={2}>RECAP IN CONVERTION UNIT</th>
                <th rowSpan={2}>AVERAGE UNIT PRICE</th>
                <th rowSpan={2}>AMOUNT ADJUSTMENT</th>
                <th rowSpan={2}>POSITION</th>
                <th rowSpan={2}>COA INVENTORY</th>
                <th rowSpan={2}>COA EXPENCES/COGS</th>
              </tr>

              <tr>
                <th>IN</th>
                <th>OUT</th>
              </tr>
            </TableHeader>

            <ProductProvider>
              <ProductUnitProvider>
                <tbody>
                  {!countdetail.length ? (
                    <tr>
                      <td colSpan={13} />
                    </tr>
                  ) : countdetail.map((item, key) => (
                    <Item
                      key={key}
                      index={key}
                      item={item}
                    />
                  ))}
                </tbody>
              </ProductUnitProvider>
            </ProductProvider>
          </BaseTable>
        </Card.Body>
      </Card>

      {params.footerElement}
    </section>
  )
}

function Code(): JSX.Element {
  // Hooks
  const { fetchCode } = useDataCode()

  // Form
  const { control, formState, setValue } = useFormContext<FormType>()
  const transaction_date = useWatch({
    control,
    name: "transaction_date"
  })
  const trx_code = useWatch({
    control,
    name: "trx_code"
  })


  useEffect(() => {
    if (!formState.defaultValues?.trx_code) {
      fetchCode(transaction_date).then(res => {
        setValue("trx_code", res)
      }).catch(err => {
        setValue("trx_code", err)
      })
    }

    // eslint-disable-next-line
  }, [transaction_date])

  return <Fragment>{trx_code}</Fragment>
}

export function Item(params: {
  index: number
  isPrint?: boolean
  item: DataCountDetail
}): JSX.Element {
  // Hooks
  const products = useContext(ProductContext)
  // const units = useContext(ProductUnitContext)
  const disabled = useContext(DisabledContext)


  // Vars
  const item = params.item
  const product = products.data.find(i => i.product_id === item.product_id)!
  const soh = item.soh_count ?? item.soh
  const recapUnit = Math.abs(Number(item.different_unit) || 0)
  const isDebet = item.position === "debet"

  // const unit = units.data.find(i => i.value === item.mr_unit_id.toString())!
  // const unitSell = Number(item.quantity_unit_sell_convention) !== 0 ? item.quantity_unit_sell_convention : 1

  return (
    <tr>
      <td>{params.index + 1}</td>
      <td>{product?.product_barcode}</td>
      <td>{product?.product_name}</td>
      <td>{item.unit_convention_name ?? '-'}</td>
      <td className="text-right">{soh != null ? convertNumber(soh).intoNormalAmount : 0}</td>
      <td className="text-right">{convertNumber(Math.abs(item.stock_in)).intoNormalAmount}</td>
      <td className="text-right">{convertNumber(Math.abs(item.stock_out)).intoNormalAmount}</td>
      <td className="text-right">{isDebet && recapUnit ? `${convertNumber(recapUnit).intoNormalAmount} ${item.unit_convention_name ?? ""}` : 0}</td>
      <td className="text-right">{!isDebet && recapUnit ? `${convertNumber(recapUnit).intoNormalAmount} ${item.unit_convention_name ?? ""}` : 0}</td>
      <td className="text-right">{convertNumber(item.avg_price_use).intoCurrency}</td>
      <td className="text-right">{convertNumber(Math.abs(item.amount_adjustment)).intoCurrency}</td>
      <td className="uppercase">{item.position}</td>

      {disabled || params?.isPrint ? (
        <>
          <td>{` ${item.coa_inventory_code} - ${item.coa_inventory_name}`}</td>
          <td>{` ${item.coa_coex_code} - ${item.coa_coex_name}`}</td>
        </>
      ) : (
        <CoaDropDown
          index={params.index}
          item={item}
        />
      )}



    </tr>
  )
}

function Employee(): JSX.Element {
  // Hooks
  const disabled = useContext(DisabledContext)
  const { dataEmployee: data, isLoadingEmployee } = useEmployee()

  // Form
  const { control } = useFormContext<FormType>()

  return (
    <Controller
      name="employee_id"
      control={control}
      render={({ field, fieldState }) => (
        <Select
          label="ADJUSTMENT BY"
          placeholder="Select Employee"
          isDisabled={disabled}
          error={fieldState.error?.message}
          isLoading={isLoadingEmployee}
          options={data}
          value={data.find(item => item.value === field.value)}
          ref={field.ref}
          onChange={res => {
            if (res) {
              field.onChange(res.value)
            }
          }}
        />
      )}
    />
  )
}

function CoaDropDown(params: {
  index: number
  item: DataCountDetail
}): JSX.Element {
  const { isActive, toggle } = useToggle(false)
  const [selectedField, setSelectedField] = useState<"coa_inventory_id" | "coa_coex_id" | null>(null)
  const { control, watch, setValue } = useFormContext<FormType>()

  const coaInventoryName = `${watch(`adjusmentDetail.${params.index}.coa_inventory_code`)} - ${watch(`adjusmentDetail.${params.index}.coa_inventory_name`)}`
  const coaCoexName = `${watch(`adjusmentDetail.${params.index}.coa_coex_code`)} - ${watch(`adjusmentDetail.${params.index}.coa_coex_name`)}`

  return (
    <>
      <td>
        <Controller
          name={`adjusmentDetail.${params.index}.coa_inventory_id`}
          control={control}
          render={({ field, fieldState }) => {

            let value: PropsValue<{
              value: string
              label: string
            }> = null

            if (field.value) {
              value = {
                label: coaInventoryName,
                value: field.value.toString()
              }
            }

            return (
              <section onClick={() => {
                setSelectedField("coa_inventory_id")
                toggle()
              }}>
                <Select
                  placeholder="Select Account"
                  error={fieldState.error?.message}
                  ref={field.ref}
                  value={value}
                />
              </section>
            )
          }}
        />
      </td>
      <td>
        <Controller
          name={`adjusmentDetail.${params.index}.coa_coex_id`}
          control={control}
          render={({ field, fieldState }) => {

            let value: PropsValue<{
              value: string
              label: string
            }> = null

            if (field.value) {
              value = {
                label: coaCoexName,
                value: field.value.toString()
              }
            }

            return (
              <section onClick={() => {
                setSelectedField("coa_coex_id")
                toggle()
              }}>
                <Select
                  placeholder="Select Account"
                  error={fieldState.error?.message}
                  ref={field.ref}
                  value={value}
                />
              </section>
            )
          }}
        />
      </td>


      {isActive && (
        <AccountModal
          defaultGroup="01"
          toggle={toggle}
          onChange={(value, label, coa) => {
            if (selectedField === "coa_coex_id") {
              setValue(`adjusmentDetail.${params.index}.coa_coex_id`, coa.coa_id)
              setValue(`adjusmentDetail.${params.index}.coa_coex_code`, coa.coa_code)
              setValue(`adjusmentDetail.${params.index}.coa_coex_name`, coa.coa_name)
            }

            if (selectedField === "coa_inventory_id") {
              setValue(`adjusmentDetail.${params.index}.coa_inventory_id`, coa.coa_id)
              setValue(`adjusmentDetail.${params.index}.coa_inventory_code`, coa.coa_code)
              setValue(`adjusmentDetail.${params.index}.coa_inventory_name`, coa.coa_name)
            }
          }}
        />
      )}
    </>
  )
}
